/**
 * Pure client-side helpers for the Collections & Favourites feature.
 *
 * Mirrors the backend pure module (`functions/shared/collections.ts`) so the two
 * stay consistent: the same case/trim-insensitive duplicate rule and the same
 * name-length boundary. `toggleInSet` powers optimistic favourite/membership
 * updates in the Redux slices — it is pure and returns a new set so the reducer
 * can apply and, on failure, roll back to the previous value.
 *
 * Requirements: 4.1, 4.2, 4.3, 5.1, 5.2, 5.3, 12.2
 */

/** Maximum allowed length of a trimmed collection name. */
export const MAX_COLLECTION_NAME_LENGTH = 100;

/** Maximum number of collections one user may own. */
export const MAX_COLLECTIONS_PER_USER = 200;

/** Result of {@link validateCollectionName}. */
export type ValidateNameResult =
  | { ok: true; value: string }
  | { ok: false; reason: string };

/**
 * Validates a proposed collection name. Accepts if and only if the trimmed
 * length is between 1 and {@link MAX_COLLECTION_NAME_LENGTH} (100) inclusive.
 * Empty / whitespace-only and over-length names are rejected. Returns the
 * trimmed value so callers submit the trimmed form.
 *
 * Requirements: 4.1, 4.2, 5.1, 5.2
 */
export function validateCollectionName(name: string): ValidateNameResult {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { ok: false, reason: 'Collection name must not be empty' };
  }
  if (trimmed.length > MAX_COLLECTION_NAME_LENGTH) {
    return {
      ok: false,
      reason: `Collection name must be at most ${MAX_COLLECTION_NAME_LENGTH} characters`,
    };
  }
  return { ok: true, value: trimmed };
}

/**
 * True if `name` matches any name in `existingNames` under the case-insensitive,
 * trim-insensitive equality rule (same rule as the backend). Callers exclude a
 * collection's own current name from `existingNames` to allow renaming to it.
 *
 * Requirements: 4.3, 5.3
 */
export function isDuplicateName(name: string, existingNames: readonly string[]): boolean {
  const normalized = name.trim().toLowerCase();
  return existingNames.some((existing) => existing.trim().toLowerCase() === normalized);
}

/**
 * Optimistic set toggle: returns a **new** Set with `id` added when `next` is
 * true and removed when `next` is false. Pure — never mutates the input, which
 * may be a Set or an array of ids. Applying the inverse (`toggleInSet(result,
 * id, !next)`) restores the original membership of `id`, which is how the slices
 * roll back a failed request.
 *
 * Requirements: 12.2
 */
export function toggleInSet(
  set: ReadonlySet<string> | readonly string[],
  id: string,
  next: boolean,
): Set<string> {
  const result = new Set(set);
  if (next) {
    result.add(id);
  } else {
    result.delete(id);
  }
  return result;
}
